import { useEffect, useId, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

import { Button } from './primitives'

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])
}

function Scrim({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-dark/35 backdrop-blur-[4px]"
      onClick={onClose}
      aria-hidden="true"
    />
  )
}

/* ── Modal ────────────────────────────────────────────────────────────────── */

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  width?: 'md' | 'lg'
}

export function Modal({ open, onClose, title, subtitle, footer, children, width = 'md' }: ModalProps) {
  const titleId = useId()
  useEscape(open, onClose)
  if (!open) return null

  return (
    <>
      <Scrim onClose={onClose} />
      {/* Centred with flex rather than a translated `start-1/2`: a physical
          offset plus a physical translate would land on the wrong edge in Urdu. */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={cn(
            'pointer-events-auto max-h-[86vh] w-full',
            'gl-h no-scrollbar overflow-y-auto rounded-[18px] p-6',
            width === 'lg' ? 'max-w-[760px]' : 'max-w-[600px]',
          )}
        >
          <div className="z">
            <div className="mb-[18px] flex items-start justify-between gap-3.5">
              <div className="min-w-0">
                <h2
                  id={titleId}
                  className="font-display text-xl font-extrabold tracking-[-0.4px] text-dark"
                >
                  {title}
                </h2>
                {subtitle ? <p className="mt-1 text-[12.5px] text-muted">{subtitle}</p> : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('act.close')}
                className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted hover:text-dark"
              >
                <X className="size-4" />
              </button>
            </div>
            {children}
            {footer ? (
              <div className="mt-5 flex justify-end gap-2.5 border-t border-muted/15 pt-4">
                {footer}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

/* ── Drawer — a detail panel that keeps the list in view ──────────────────── */

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
}: ModalProps) {
  const titleId = useId()
  useEscape(open, onClose)
  if (!open) return null

  return (
    <>
      <Scrim onClose={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'fixed inset-y-0 end-0 z-50 flex w-full max-w-[460px] flex-col',
          'gl-h no-scrollbar rounded-none border-y-0 border-e-0',
        )}
      >
        <div className="z flex h-full flex-col">
          <header className="flex items-start justify-between gap-3 border-b border-muted/15 p-5">
            <div className="min-w-0">
              <h2 id={titleId} className="font-display text-lg font-extrabold text-dark">
                {title}
              </h2>
              {subtitle ? <p className="mt-1 text-[12.5px] text-muted">{subtitle}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('act.close')}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted hover:text-dark"
            >
              <X className="size-4" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-5">{children}</div>
          {footer ? (
            <footer className="flex justify-end gap-2.5 border-t border-muted/15 p-5">{footer}</footer>
          ) : null}
        </div>
      </aside>
    </>
  )
}

/* ── ConfirmDialog ────────────────────────────────────────────────────────
   Every destructive action goes through this. An IRREVERSIBLE one additionally
   requires the business name to be typed: the point is not friction, it is that
   a person who cannot name the thing they are deleting is on the wrong row.
   ────────────────────────────────────────────────────────────────────────── */

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: ReactNode
  body?: ReactNode
  confirmLabel: string
  tone?: 'danger' | 'accent'
  /** Supply to switch on type-to-confirm. The exact phrase the reader must type. */
  typeToConfirm?: string
  /** An extra consequence line, stated in the reader's own terms. */
  consequence?: ReactNode
  children?: ReactNode
  /** Set when the confirm button must stay disabled for a reason of your own. */
  disabled?: boolean
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  tone = 'danger',
  typeToConfirm,
  consequence,
  children,
  disabled = false,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const inputId = useId()

  useEffect(() => {
    if (!open) setTyped('')
  }, [open])

  const matches = !typeToConfirm || typed.trim() === typeToConfirm
  const ready = matches && !disabled

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            disabled={!ready}
            onClick={() => {
              onConfirm()
              onClose()
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body ? <p className="text-[13.5px] leading-relaxed text-sub">{body}</p> : null}
      {children ? <div className="mt-4">{children}</div> : null}
      {consequence ? (
        <p className="mt-4 rounded-r border border-danger/25 bg-danger/5 p-3 text-[12.5px] leading-relaxed text-danger">
          {consequence}
        </p>
      ) : null}
      {typeToConfirm ? (
        <div className="mt-5">
          <label htmlFor={inputId} className="mb-1.5 block text-[13px] font-semibold text-dark">
            {t('confirm.typeToConfirm', { phrase: typeToConfirm })}
          </label>
          <input
            id={inputId}
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
            className={cn(
              'h-10 w-full rounded-[10px] border border-glass-border bg-glass px-3.5',
              'text-[13.5px] font-semibold text-dark outline-none',
            )}
          />
          {typed && !matches ? (
            <p className="mt-1.5 text-xs text-muted">{t('confirm.typeMismatch')}</p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  )
}
